import { useEffect, useRef } from "react";

const TRIANGLE_EDGE = 44;
const ROW_HEIGHT = TRIANGLE_EDGE * Math.sin(Math.PI / 3);

const LARGE_RADIUS = 512;
const LARGE_SOLID_RADIUS = LARGE_RADIUS * 0.3;

const BUBBLE_RADIUS = 256;
const BUBBLE_STRENGTH = 2

type PointerState = {
  x: number;
  y: number;
  active: boolean;
};

type MeshVertex = {
  baseX: number;
  baseY: number;
  x: number;
  y: number;
  bubble: number;
};

function smoothCosineFalloff(distance: number, radius: number): number {
  if (distance >= radius) return 0;
  return 0.5 + 0.5 * Math.cos((Math.PI * distance) / radius);
}

function largeHaloFalloff(distance: number): number {
  if (distance <= LARGE_SOLID_RADIUS) return 1;
  if (distance >= LARGE_RADIUS) return 0;

  const t = (distance - LARGE_SOLID_RADIUS) / (LARGE_RADIUS - LARGE_SOLID_RADIUS);
  return 0.5 + 0.5 * Math.cos(Math.PI * t);
}

function baseVertex(row: number, col: number): { x: number; y: number } {
  const rowOffset = row % 2 === 0 ? 0 : TRIANGLE_EDGE / 2;
  return {
    x: col * TRIANGLE_EDGE + rowOffset,
    y: row * ROW_HEIGHT,
  };
}

function displacedVertex(row: number, col: number, pointer: PointerState): MeshVertex {
  const { x: baseX, y: baseY } = baseVertex(row, col);
  const dx = baseX - pointer.x;
  const dy = baseY - pointer.y;
  const distance = Math.hypot(dx, dy);
  // The raised bubble is geometric: nearby mesh vertices move away from the
  // cursor before edges are drawn, instead of adding a second visual layer.
  const bubble = smoothCosineFalloff(distance, BUBBLE_RADIUS);

  if (bubble === 0) {
    return { baseX, baseY, x: baseX, y: baseY, bubble };
  }

  const inverseDistance = distance > 0.001 ? 1 / distance : 0;
  const directionX = distance > 0.001 ? dx * inverseDistance : 0;
  const directionY = distance > 0.001 ? dy * inverseDistance : -1;
  const offset = BUBBLE_STRENGTH * bubble;

  return {
    baseX,
    baseY,
    x: baseX + directionX * offset,
    y: baseY + directionY * offset,
    bubble,
  };
}

function edgeVisibility(a: MeshVertex, b: MeshVertex, pointer: PointerState): number {
  const midX = (a.baseX + b.baseX) / 2;
  const midY = (a.baseY + b.baseY) / 2;
  return Math.max(
    largeHaloFalloff(Math.hypot(a.baseX - pointer.x, a.baseY - pointer.y)),
    largeHaloFalloff(Math.hypot(b.baseX - pointer.x, b.baseY - pointer.y)),
    largeHaloFalloff(Math.hypot(midX - pointer.x, midY - pointer.y))
  );
}

function drawEdge(
  ctx: CanvasRenderingContext2D,
  a: MeshVertex,
  b: MeshVertex,
  pointer: PointerState
): void {
  const visibility = edgeVisibility(a, b, pointer);
  if (visibility <= 0.01) return;

  const bubbleLift = Math.max(a.bubble, b.bubble);
  const alpha = Math.min(0.68, 0.25 * visibility + 0.18 * bubbleLift);

  ctx.lineWidth = 1 + bubbleLift * 0.7;
  ctx.strokeStyle = `rgba(91, 179, 255, ${alpha})`;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
}

function drawVertex(ctx: CanvasRenderingContext2D, vertex: MeshVertex): void {
  if (vertex.bubble <= 0.08) return;

  const radius = 0.8 + vertex.bubble * 1.3;
  ctx.fillStyle = `rgba(141, 203, 255, ${0.14 + vertex.bubble * 0.001})`;
  ctx.beginPath();
  ctx.arc(vertex.x, vertex.y, radius, 0, Math.PI * 2);
  ctx.fill();
}

export function TriangleGrid() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(hover: hover)").matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const pointer: PointerState = { x: -400, y: -400, active: false };
    let frame = 0;
    let width = 0;
    let height = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.ceil(width * dpr);
      canvas.height = Math.ceil(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const render = () => {
      frame = 0;
      ctx.clearRect(0, 0, width, height);
      if (!pointer.active) return;

      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      const margin = LARGE_RADIUS + BUBBLE_STRENGTH + TRIANGLE_EDGE;
      const rowStart = Math.floor((pointer.y - margin) / ROW_HEIGHT) - 1;
      const rowEnd = Math.ceil((pointer.y + margin) / ROW_HEIGHT) + 1;
      const colStart = Math.floor((pointer.x - margin) / TRIANGLE_EDGE) - 2;
      const colEnd = Math.ceil((pointer.x + margin) / TRIANGLE_EDGE) + 2;

      for (let row = rowStart; row <= rowEnd; row += 1) {
        for (let col = colStart; col <= colEnd; col += 1) {
          const vertex = displacedVertex(row, col, pointer);
          drawEdge(ctx, vertex, displacedVertex(row, col + 1, pointer), pointer);

          if (row % 2 === 0) {
            drawEdge(ctx, vertex, displacedVertex(row + 1, col, pointer), pointer);
            drawEdge(ctx, vertex, displacedVertex(row + 1, col - 1, pointer), pointer);
          } else {
            drawEdge(ctx, vertex, displacedVertex(row + 1, col, pointer), pointer);
            drawEdge(ctx, vertex, displacedVertex(row + 1, col + 1, pointer), pointer);
          }

          drawVertex(ctx, vertex);
        }
      }
    };

    const requestRender = () => {
      if (frame === 0) frame = requestAnimationFrame(render);
    };

    const onPointerMove = (event: PointerEvent) => {
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.active = true;
      requestRender();
    };

    const onPointerLeave = () => {
      pointer.active = false;
      requestRender();
    };

    const onResize = () => {
      resize();
      requestRender();
    };

    resize();
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("resize", onResize);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="triangle-grid-canvas pointer-events-none fixed inset-0 z-0 block"
      aria-hidden="true"
    />
  );
}
